import { useState, useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getRoleDashboard } from '@/components/feature/AuthGuard';
import { useSetting } from '@/hooks/useSetting';
import { eventInfo } from '@/mocks/landing-data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL + '/functions/v1';

const MFA_ROLES = ['admin', 'organizer'];

type LoginStep = 'credentials' | 'otp';

export default function Login() {
  // ── All hooks at the top (React rules: no conditional hooks) ──
  const { user, profile, loading: authLoading, signIn, mfaVerified, setMfaVerified } = useAuth();
  const eventName = useSetting('event_name', eventInfo.name);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState<string | null>(null);
  const [loginInProgress, setLoginInProgress] = useState(false);

  // MFA state
  const [step, setStep] = useState<LoginStep>('credentials');
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [mfaProfileRole, setMfaProfileRole] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [otpMessage, setOtpMessage] = useState('');

  const mfaGenerationTriggered = useRef(false);

  // ── Helper: start resend cooldown ──
  const startResendCooldown = () => {
    setOtpResendCooldown(60);
    const timer = setInterval(() => {
      setOtpResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // ── Helper: go back to credentials from OTP ──
  const handleBackToCredentials = () => {
    setStep('credentials');
    setRequiresMfa(false);
    setOtp('');
    setError('');
    setOtpMessage('');
    setLoginInProgress(false);
    mfaGenerationTriggered.current = false;
  };

  // ── Effect: detect session-without-MFA and force OTP page ──
  // Covers: page refresh during OTP, AuthGuard redirect, direct navigation
  useEffect(() => {
    if (user && profile && MFA_ROLES.includes(profile.role) && !mfaVerified && !loginInProgress) {
      if (mfaGenerationTriggered.current) return;
      mfaGenerationTriggered.current = true;

      setEmail(profile.email || user.email || '');
      setMfaProfileRole(profile.role);
      setRequiresMfa(true);
      setStep('otp');
      setError('');

      fetch(`${FUNCTIONS_URL}/mfa-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'generate', email: profile.email || user.email }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.error) {
            setError(data.error);
          } else {
            setOtpMessage(data.message || 'OTP sent to your email');
            startResendCooldown();
          }
        })
        .catch(() => {
          setError('Unable to reach the verification service. Please check your internet connection and try again.');
        });
    }
  }, [user, profile, mfaVerified, loginInProgress]);

  // ── Computed ──
  const needsMfa = !!(profile && MFA_ROLES.includes(profile.role) && !mfaVerified);

  // ── Early returns after ALL hooks ──

  // MFA already verified — shouldn't be on login page, redirect
  if (mfaVerified && user && profile) {
    const dashboard = getRoleDashboard(profile.role);
    return <Navigate to={dashboard} replace />;
  }

  // After successful OTP, mfaVerified=true → pendingRedirect fires
  if (pendingRedirect) {
    return <Navigate to={pendingRedirect} replace />;
  }

  // ⛔ Only auto-redirect when session is valid AND MFA is not needed
  // A valid session alone is NEVER sufficient for Admin/Organiser
  if (!loginInProgress && user && profile && !needsMfa) {
    const dashboard = getRoleDashboard(profile.role);
    return <Navigate to={dashboard} replace />;
  }

  if (user && !profile && authLoading === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin mb-4"></div>
          <p className="text-sm text-foreground-500">Loading your account...</p>
        </div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setOtpSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${FUNCTIONS_URL}/mfa-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify', email: email.trim(), otp }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        if (data.attemptsRemaining !== undefined) {
          setError(`Invalid OTP. ${data.attemptsRemaining} attempt${data.attemptsRemaining !== 1 ? 's' : ''} remaining.`);
        } else {
          setError(data.error || 'OTP verification failed');
        }

        if (data.error && data.error.includes('Too many')) {
          setStep('credentials');
          setRequiresMfa(false);
          setOtp('');
          setLoginInProgress(false);
          mfaGenerationTriggered.current = false;
        }
        setOtpSubmitting(false);
        return;
      }

      // ⛔ Set MFA verified BEFORE any redirect — this is what prevents the bypass
      setMfaVerified(true);
      setLoginInProgress(false);
      mfaGenerationTriggered.current = false;

      // Log the successful MFA login (fire-and-forget)
      fetch(`${FUNCTIONS_URL}/manage-login-attempts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', email: email.trim() }),
      }).catch(() => {});

      if (mfaProfileRole) {
        const dashboard = getRoleDashboard(mfaProfileRole);
        setPendingRedirect(dashboard);
      }
    } catch {
      setError('A network error occurred. Please try again.');
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpResendCooldown > 0) return;
    setError('');
    setOtpMessage('');

    try {
      const res = await fetch(`${FUNCTIONS_URL}/mfa-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'resend', email: email.trim() }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setOtpMessage(data.message || 'New OTP sent');
        setOtp('');
        startResendCooldown();
      } else {
        setError(data.error || 'Failed to resend OTP');
      }
    } catch {
      setError('Network error. Please try again.');
    }
  };

  if (requiresMfa && step === 'otp') {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16 pb-12 bg-background-50">
          <div className="w-full max-w-md mx-auto px-4">
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-14 h-14 mx-auto rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-shield-keyhole-line text-2xl text-primary-600"></i>
                </div>
                <h1 className="font-heading text-2xl font-bold text-foreground-950">Two-Factor Authentication</h1>
                <p className="text-sm text-foreground-500 mt-1">
                  A verification code has been sent to <strong className="text-foreground-700">{email}</strong>
                </p>
              </div>

              <form onSubmit={handleOtpSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-start gap-2">
                    <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                    <span>{error}</span>
                  </div>
                )}

                {otpMessage && !error && (
                  <div className="p-3 rounded-lg bg-accent-50 border border-accent-200 text-accent-700 text-sm flex items-start gap-2">
                    <i className="ri-check-line mt-0.5 flex-shrink-0"></i>
                    <span>{otpMessage}</span>
                  </div>
                )}

                {error && error.includes('expired') ? (
                  <div className="text-center pt-2">
                    <p className="text-sm text-foreground-500">Your verification code has expired.</p>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={otpResendCooldown > 0}
                      className="mt-2 text-primary-600 hover:text-primary-700 font-semibold text-sm cursor-pointer"
                    >
                      {otpResendCooldown > 0 ? `Resend in ${otpResendCooldown}s` : 'Request a new OTP'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="login-otp">Enter 6-digit OTP</label>
                      <input
                        id="login-otp"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={otp}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setOtp(val);
                        }}
                        className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-center text-2xl tracking-[0.5em] font-mono focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors"
                        placeholder="000000"
                        maxLength={6}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={otpSubmitting || otp.length !== 6}
                      className="w-full py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {otpSubmitting && <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin"></div>}
                      {otpSubmitting ? 'Verifying...' : 'Verify & Continue'}
                    </button>

                    <div className="text-center pt-2">
                      <p className="text-sm text-foreground-500">
                        Didn&apos;t receive the code?{' '}
                        {otpResendCooldown > 0 ? (
                          <span className="text-foreground-400">Resend in {otpResendCooldown}s</span>
                        ) : (
                          <button type="button" onClick={handleResendOtp} className="text-primary-600 hover:text-primary-700 font-semibold cursor-pointer">
                            Resend OTP
                          </button>
                        )}
                      </p>
                    </div>
                  </>
                )}

                <div className="pt-3 border-t border-background-200 text-center">
                  <button
                    type="button"
                    onClick={handleBackToCredentials}
                    className="text-sm text-foreground-400 hover:text-foreground-600 cursor-pointer"
                  >
                    &larr; Back to login
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const doCredentialLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    setLoginInProgress(true);

    try {
      const checkRes = await fetch(`${FUNCTIONS_URL}/manage-login-attempts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'check', email: email.trim() }),
      });
      const checkData = await checkRes.json();

      if (checkData.locked) {
        setError('Your account has been locked after 3 unsuccessful login attempts. Please contact the administrator to unlock your account.');
        setSubmitting(false);
        setLoginInProgress(false);
        return;
      }

      const signInResult = await signIn(email.trim(), password);

      if (signInResult.error) {
        const failRes = await fetch(`${FUNCTIONS_URL}/manage-login-attempts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'record_failed', email: email.trim() }),
        });
        const failData = await failRes.json();

        if (failData.locked) {
          setError('Your account has been locked after 3 unsuccessful login attempts. Please contact the administrator to unlock your account.');
        } else if (signInResult.error.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.');
        } else if (signInResult.error.includes('Email not confirmed')) {
          setError('Please verify your email before signing in. Check your inbox.');
        } else {
          setError(signInResult.error);
        }
        setSubmitting(false);
        setLoginInProgress(false);
        return;
      }

      // Reset failed attempts after successful password verification
      await fetch(`${FUNCTIONS_URL}/manage-login-attempts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reset', email: email.trim() }),
      });

      const userRole = signInResult.profile?.role || checkData.role;

      if (MFA_ROLES.includes(userRole)) {
        // MFA required — keep loginInProgress true until OTP verified
        setMfaProfileRole(userRole);
        setRequiresMfa(true);
        setStep('otp');
        setSubmitting(false);
        mfaGenerationTriggered.current = true;

        // OTP generation wrapped in its own try-catch:
        // failures here must NOT propagate to the outer catch
        // (which would reset loginInProgress and cause a bypass)
        try {
          const otpRes = await fetch(`${FUNCTIONS_URL}/mfa-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'generate', email: email.trim() }),
          });
          const otpData = await otpRes.json();
          if (otpData.error) {
            setError(otpData.error);
          } else {
            setOtpMessage(otpData.message || 'OTP sent to your email');
            startResendCooldown();
          }
        } catch (_otpErr) {
          setError('Failed to send verification code. Please try again or request a resend.');
        }
        // loginInProgress STAYS true — return from MFA branch
        return;
      }

      // No MFA required (participant / accounts)
      fetch(`${FUNCTIONS_URL}/manage-login-attempts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', email: email.trim() }),
      }).catch(() => {});

      if (signInResult.profile) {
        setLoginInProgress(false);
        const dashboard = getRoleDashboard(signInResult.profile.role);
        setPendingRedirect(dashboard);
      } else {
        setError('Your account exists but your profile is incomplete. Please contact the event administrator.');
        setLoginInProgress(false);
      }
    } catch {
      setError('A network error occurred. Please try again.');
      setLoginInProgress(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen flex items-center justify-center pt-16 pb-12 bg-background-50">
        <div className="w-full max-w-md mx-auto px-4">
          <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 mx-auto rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                <i className="ri-heart-pulse-line text-2xl text-primary-600"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950">Welcome Back</h1>
              <p className="text-sm text-foreground-500 mt-1">Sign in to your {eventName} account</p>
            </div>

            <form onSubmit={doCredentialLogin} className="space-y-4">
              {error && (
                <div className={`p-3 rounded-lg text-sm ${error.includes('locked') ? 'bg-amber-50 border border-amber-200 text-amber-700' : 'bg-red-50 border border-red-200 text-red-600'}`}>
                  {error.includes('locked') && (
                    <div className="flex items-start gap-2">
                      <i className="ri-lock-line text-base mt-0.5 flex-shrink-0"></i>
                      <span>{error}</span>
                    </div>
                  )}
                  {!error.includes('locked') && error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="login-email">Email Address</label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors placeholder:text-foreground-400"
                  placeholder="team@example.com"
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="login-password">Password</label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 pr-10 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors placeholder:text-foreground-400"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                    tabIndex={-1}
                  >
                    <i className={`text-base ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded accent-primary-500"
                  />
                  <span className="text-sm text-foreground-600">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {submitting && <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin"></div>}
                {submitting ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-background-200 text-center">
              <p className="text-sm text-foreground-500">
                Don&apos;t have a team?{' '}
                <Link to="/register" className="text-primary-600 hover:text-primary-700 font-semibold cursor-pointer">
                  Register here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}